/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';

import { Text } from 'components/common/Elements/Elements.js';

import { createOptions } from './helpers/helpers';

import {
  DropdownIndicatorPart,
  ClearIndicatorPart,
  OptionPart,
  MenuListPart,
  PlaceholderPart
} from './Parts/Parts';
import { selectStyles } from './Parts/selectStyles';

import classes from './SelectAccordion.module.scss';

const SelectAccordion = ({
  options,
  input,
  limit,
  placeholder = 'Select Features',
  groupSelectPlaceholder,
  modeAccordion = true,
  styleStartModeSmall = true,
  title,
  offTrendline,
  trigerReset,
  closeTrigger
}) => {
  // for storing options received from the backend
  const [list, setList] = useState([]);
  // for store all list items with additional properties
  const [allValueOptions, setAllValueOptions] = useState([]);
  // the list that will be displayed in the select
  const [menuList, setMenuList] = useState([]);
  // for storing the state of groups (open - closed)
  const [groupStatus, setGroupStatus] = useState({});
  // the state of the fields "select all" and the number of options in the group
  const [selectAllStatus, setSelectAllStatus] = useState({});
  // trigger, indicating that the list of options has been received
  const [trigerGetOptions, setTrigerGetOptions] = useState(false);

  const [triger, setTriger] = useState(false);

  const { value, onChange } = input;

  const { t } = useTranslation(['activity']);

  useEffect(() => {
    // save the list of options. We form objects of states of groups and buttons "select all"
    if (options && options.length > 0 && !trigerGetOptions) {
      createListOptions();
    }
    // eslint-disable-next-line
  }, [options]);

  useEffect(() => {
    if (!isEqual(options, list)) {
      createListOptions();
    }
    // eslint-disable-next-line
  }, [options, trigerReset]);

  const createListOptions = () => {
    setTrigerGetOptions(true);
    setList(options);
    options.forEach(item => {
      setGroupStatus(prevState => ({
        ...prevState,
        [item.label.value]: false
      }));
      setSelectAllStatus(prevState => ({
        ...prevState,
        [item.label.value]: { status: false, length: item.options.length }
      }));
    });
  };

  useEffect(() => {
    const listGroups = Object.keys(selectAllStatus);

    // when updating Input (value), we count how many options are selected
    // from the groups for the automatic "select all" state (check or not check)
    listGroups.forEach(group => {
      if (allValueOptions.length > 0) {
        let count = 0;
        // counting how many options are selected from the group
        value.forEach(item => {
          const infoValue = allValueOptions.find(option => {
            return option.value === item;
          });

          if (infoValue.nameParent === group) {
            count++;
          }
        });
        //if the number of selected elements is equal to the number
        // of elements in the group - change the state to "true", otherwise to "false"
        if (selectAllStatus[group].length === count) {
          setSelectAllStatus(prevState => ({
            ...prevState,
            [group]: { ...prevState[group], status: true }
          }));
        } else if (selectAllStatus[group].length > count) {
          setSelectAllStatus(prevState => ({
            ...prevState,
            [group]: { ...prevState[group], status: false }
          }));
        }
      }
    });
    // eslint-disable-next-line
  }, [input]);

  useEffect(() => {
    // creating a list of all possible options with additional
    // properties (the option belongs to a group, the "select all"
    // or "One Trendline" button belongs to a group)
    let optionsList = [];
    list.forEach(option => {
      option.options.forEach(item => {
        optionsList.push({
          value: item.value,
          label: item.label,
          nameParent: option.label.value
        });
      });
      if (!offTrendline || groupSelectPlaceholder) {
        optionsList.push({
          value: option.label.value,
          label: groupSelectPlaceholder
            ? groupSelectPlaceholder
            : t('activity:filters.ONE_TRENDLINE'),
          nameCat: option.label.value,
          placeholderName: option.label.label
        });
      }
    });
    setAllValueOptions(optionsList);
    // eslint-disable-next-line
  }, [list]);

  useEffect(() => {
    // form a list of options to display in the select
    let optionsList = [];

    list.forEach(option => {
      // add a group
      optionsList.push({
        group: option.label.value,
        label: option.label.label,
        openGroup: option.label.openGroup
      });
      // if the status of the group is "true" - add the group options to the list
      if (groupStatus[option.label.value]) {
        // button Select All
        optionsList.push({
          value: `all-${option.label.value}`,
          label: t('activity:filters.SELECT_ALL'),
          nameGroup: option.label.value
        });
        // options
        option.options.forEach(item => {
          optionsList.push({
            value: item.value,
            label: item.label,
            nameParent: option.label.value
          });
        });
        // button one trendline
        if (!offTrendline || groupSelectPlaceholder) {
          optionsList.push({
            value: option.label.value,
            label: groupSelectPlaceholder
              ? groupSelectPlaceholder
              : t('activity:filters.ONE_TRENDLINE'),
            nameCat: option.label.value,
            placeholderName: option.label.label
          });
        }
      }
    });
    setMenuList(optionsList);
    // eslint-disable-next-line
  }, [list, groupStatus]);

  const handleSelectChange = (changeValue, info) => {
    // checking what is selected in the selection (a group is selected, an option is
    // selected or the "select all" or "one trendline" buttons are selected )
    // and performing actions -

    const action = info.action;
    let selectOption = {};

    if (action === 'pop-value') {
      selectOption = info.removedValue;
    } else {
      selectOption = info.option;
    }

    // if you selected a group, then change the value of the group state (open - closed)
    if (
      changeValue &&
      changeValue.length !== 0 &&
      selectOption.openGroup !== undefined
    ) {
      if (!modeAccordion) {
        setGroupStatus({
          ...groupStatus,
          [selectOption.group]: !groupStatus[selectOption.group]
        });
        return;
      } else {
        Object.keys(groupStatus).forEach(item => {
          setGroupStatus(prevState => ({
            ...prevState,
            [item]:
              item !== selectOption.group
                ? false
                : !groupStatus[selectOption.group]
          }));
        });
      }
    } else if (
      changeValue &&
      changeValue.length !== 0 &&
      selectOption.nameCat !== undefined
    ) {
      // if the "one trendline" button is pressed - change the value
      // of the "select all" state and remove the selected options from this group
      setSelectAllStatus({
        ...selectAllStatus,
        [selectOption.nameCat]: {
          ...selectAllStatus[selectOption.nameCat],
          status: false
        }
      });
      const valueOnlyGroup = changeValue.filter(value => {
        return value.nameParent !== selectOption.nameCat;
      });
      if (valueOnlyGroup.length < limit + 1 || !limit) {
        onChange(
          valueOnlyGroup.map(item => {
            return item.value;
          })
        );
      }
      return;
    } else if (
      action === 'clear' ||
      (changeValue && changeValue.length === 0)
    ) {
      // if the button "clear the list" is pressed, then remove all
      // selected options and change the value of the states of the buttons "select all"
      Object.keys(selectAllStatus).forEach(item => {
        setSelectAllStatus(prevState => ({
          ...prevState,
          [item]: { ...prevState[item], status: false }
        }));
      });
      onChange([]);
    } else if (
      ((changeValue && changeValue.length !== 0) || changeValue === null) &&
      (action === 'select-option' ||
        action === 'deselect-option' ||
        action === 'pop-value') &&
      selectOption &&
      !selectOption.nameGroup
    ) {
      // if the option is selected, change the value of the "select all"
      // state to "false" and add the selected option. (since the name of
      // the group is also considered the selected option - remove it from the list)
      let valueWithOutGroup;
      if (changeValue && changeValue.length > 0) {
        valueWithOutGroup = changeValue.filter(value => {
          return !value.nameCat || value.nameCat !== selectOption.nameParent;
        });
      } else if (changeValue === null) {
        valueWithOutGroup = [];
      }
      if (valueWithOutGroup.length < limit + 1 || !limit) {
        setSelectAllStatus({
          ...selectAllStatus,
          [selectOption.nameParent]: {
            ...selectAllStatus[selectOption.nameParent],
            status: false
          }
        });
        onChange(
          valueWithOutGroup.map(item => {
            return item.value;
          })
        );
      }
    } else if (selectOption && selectOption.nameGroup) {
      // if the "select all" button is pressed, call the function
      //  to select all elements of the group
      changeAllItems(changeValue, info);
    }
  };

  // function to activate or deactivate all elements belonging to
  // a group by clicking the "select all" button
  const changeAllItems = (changeValue, info) => {
    const selectOption = info.option;
    let offSelect;

    // if the value of the state of the button "select all" is "true" -
    // remove from the selected options options belonging to the group
    if (selectAllStatus[selectOption.nameGroup].status) {
      offSelect = changeValue.filter(
        value => value.nameParent !== selectOption.nameGroup
      );
      offSelect.pop();
    } else if (!selectAllStatus[selectOption.nameGroup].status) {
      // if the value of the state of the "select all" button is "false" -
      // add the missing elements of the group to the list of selected options
      offSelect = changeValue;
      offSelect.pop();
      menuList.forEach(value => {
        if (
          value.nameParent &&
          value.nameParent === selectOption.nameGroup &&
          !changeValue.find(item => {
            return item.value === value.value;
          })
        ) {
          offSelect.push(value);
        }
      });
    }
    // since the list contains the buttons "one trendline" we
    // remove them from the list except those from other groups
    const valueWithOutGroup = offSelect.filter(value => {
      return !value.nameCat || value.nameCat !== selectOption.nameGroup;
    });
    // change the value of the state of the "select to all" button
    // to the opposite of which it was
    if (valueWithOutGroup.length < limit + 1 || !limit) {
      setSelectAllStatus({
        ...selectAllStatus,
        [selectOption.nameGroup]: {
          ...selectAllStatus[selectOption.nameGroup],
          status: !selectAllStatus[selectOption.nameGroup].status
        }
      });
      // return the "value" property of the selected values
      onChange(
        valueWithOutGroup.map(item => {
          return item.value;
        })
      );
    }
  };
  return (
    <div className={classes.wrapSelect}>
      {title && <Text className={classes.label}>{title}</Text>}
      <Select
        onMenuClose={() => setTriger(false)}
        onMenuOpen={() => setTriger(true)}
        setTrigger={setTriger}
        triger={triger}
        offTrendline={offTrendline}
        styleStartModeSmall={styleStartModeSmall}
        allValueOptions={allValueOptions}
        placeholder={placeholder}
        groupStatus={groupStatus}
        selectAllStatus={selectAllStatus}
        limit={limit}
        value={value ? createOptions(value, allValueOptions) : []}
        isSearchable={false}
        isMulti={true}
        closeMenuOnSelect={false}
        menuPlacement={'bottom'}
        components={{
          IndicatorSeparator: null,
          DropdownIndicator: DropdownIndicatorPart,
          ClearIndicator: ClearIndicatorPart,
          Option: OptionPart,
          MenuList: MenuListPart,
          Placeholder: PlaceholderPart
        }}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        onChange={handleSelectChange}
        options={menuList}
        styles={selectStyles}
        tabSelectsValue={false}
      />
    </div>
  );
};

SelectAccordion.propTypes = {
  options: PropTypes.array.isRequired,
  input: PropTypes.object.isRequired,
  limit: PropTypes.number,
  placeholder: PropTypes.string,
  groupSelectPlaceholder: PropTypes.string,
  modeAccordion: PropTypes.bool,
  title: PropTypes.string,
  styleStartModeSmall: PropTypes.bool,
  offTrendline: PropTypes.bool,
  trigerReset: PropTypes.bool,
  closeTrigger: PropTypes.shape({
    trigger: PropTypes.bool.isRequired,
    setTrigger: PropTypes.func.isRequired
  })
};

export default SelectAccordion;
